import React, { ReactNode, PropsWithChildren, useState, useEffect } from 'react';
import Dialog from '@mui/material//Dialog';
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import { Scrollbars } from "react-custom-scrollbars";
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import { Box, DialogProps } from "@mui/material";
import './style.css'
import {FormsHelpLinks} from "constants/common";

interface CommonDialogBoxProps extends PropsWithChildren<{}> {
    open: boolean;
    classes?: any;
	handleClose?: DialogProps["onClose"];
    fullWidth?: boolean;
    maxWidth?: string;
    maxHeight?: number|string;
    options?: any;
    children?: ReactNode;
    dialogContent?: React.ReactNode;
    dialogHeader?: ReactNode;
	dialogAction?: React.ReactNode;
    subtitle1?: string;
    titleh5?: string;
    actionClasses?: string;
    displayScrollBar?: boolean;
    ignoreBackdropClick?: boolean;
    helpLink?: string;
    helpLinkCode?: string;
    noHeader?: boolean;
}

interface HeaderProps {
    subtitle1?: string;
    titleh5?: string;
    helpLink?: string;
    helpLinkCode?: string;
    handleClose?: DialogProps["onClose"];
}

const Header = ({subtitle1, titleh5, helpLink, helpLinkCode, handleClose}: HeaderProps) => {
    const helpLinkUrl = helpLinkCode && FormsHelpLinks[helpLinkCode] ? FormsHelpLinks[helpLinkCode] : helpLink;

    return (
        <>
            <Toolbar className="form-header flex w-full">
                <Typography variant="subtitle1" color="inherit">{subtitle1}</Typography>
            </Toolbar>
            { titleh5 ? (
                <div className="form-subheader flex flex-col justify-center pb-24">
                    <Typography variant="h5" color="inherit" className="pt-8">{titleh5}</Typography>
                </div>
            ): <></> }
            { helpLinkUrl ? (
                <IconButton
                    aria-label="help"
                    onClick={(event: any) => {
                        event.preventDefault();
                        window.open(helpLinkUrl, '_blank')?.focus();
                    }}
                    sx={(theme) => ({
                        position: 'absolute',
                        right: 48,
                        top: 8,
                        color: theme.palette.grey[500],
                    })}
                    title='Help'
                >
                    <HelpOutlineOutlinedIcon />
                </IconButton>
            ) : ""}
            <IconButton
                aria-label="close"
                onClick={(event: any) => {
                    handleClose && handleClose(event, 'escapeKeyDown')
                }}
                sx={(theme) => ({
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: theme.palette.grey[500],
                })}
            >
                <CloseIcon />
            </IconButton>
        </>
    )
}

const CommonDialogBox = (props: CommonDialogBoxProps) => {
	const { 
        open,
		classes,
		handleClose,
		subtitle1,
		titleh5,
		dialogHeader,
		dialogContent,
		dialogAction,
		children,
        fullWidth=true,
        maxWidth="sm",
        options,
        actionClasses="",
        displayScrollBar=true,
        ignoreBackdropClick=false,
        maxHeight=300,
        helpLink,
        helpLinkCode,
        noHeader
	} = props;

    const [boxState, setBoxState] = useState<boolean>(false);

	const onClose:  DialogProps["onClose"] = (event, reason) => {
        if (ignoreBackdropClick && reason && reason === "backdropClick") return;
        if (handleClose) {
            handleClose(event, reason);
            return;
        }
        setBoxState(!boxState);
    }

    useEffect(() => {
        setBoxState(open);
    }, [open])

	return (
		<Dialog
			open={boxState}
			onClose={onClose}
			classes={{
				...classes,
				paper: `${classes?.paper || 'white-bg'} CommonDialogBox`
			}}
            fullWidth={fullWidth}
            maxWidth={maxWidth}
			{...options}
		>
            { !noHeader && (
                <AppBar position="static" elevation={1} className='no-bg'>
                    { dialogHeader ? 
                        dialogHeader : (
                        <Header 
                            subtitle1={subtitle1}
                            titleh5={titleh5}
                            handleClose={handleClose}
                            helpLink={helpLink}
                            helpLinkCode={helpLinkCode}
                        />
                    )}
                </AppBar>
            )}
            <DialogContent classes={{ root: 'p-24' }}>
                { dialogContent ? dialogContent : (
                    !displayScrollBar ? children : (
                        <Scrollbars
                                autoHeight
                                autoHeightMax={maxHeight}
                                style={{ overflowX: "hidden", width: "100%," }}
                        >
                            <Box p={2}>
                                {children}
                            </Box>
                        </Scrollbars>
                    )
                )}
            </DialogContent>
            { dialogAction ? (
                <DialogActions className={`justify-between formfooter ${actionClasses}`}>{dialogAction}</DialogActions>
            ) : <></>}
        </Dialog>
	);
}

export default CommonDialogBox;
